import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleAlertIcon, CircleMinusIcon, CircleXIcon } from '@persona-id/design-tokens/icons';
import { isEmpty } from 'lodash/fp';
import { BORDER_RADIUSES, COLORS } from 'shared/styles/variables';
import styled from 'styled-components';

import ClickableDetail from 'dashboard/components/ClickableDetail';
import ConnectedReportMenu from 'dashboard/components/ConnectedReportMenu';
import { INPUTLESS_REPORTS } from 'dashboard/components/ReportDetails/ReportDetails';
import { ReportInput } from 'dashboard/components/ReportInput';
import ReportModuleHeader from 'dashboard/components/ReportModuleHeader';
import ReportResult from 'dashboard/components/ReportResult';
import Banner from 'dashboard/components-lib/Banner';
import { Report, ReportStatus, RequiredField } from 'dashboard/models/report';
import { ReportTemplate } from 'dashboard/models/report-template';
import { Tag } from 'dashboard/models/tag';
import ResourceTags from 'dashboard/modules/Resource/ResourceTags';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import useCurrentEnvironmentType from 'lib/hooks/useCurrentEnvironmentType';
import { ApiModel } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import { Permission } from 'lib/permissions';
import { assertNever } from 'lib/utils/types';

const MissingRequiredFieldsSpan = styled('h4')`
  color: ${Color.CoreRed900};
  padding-bottom: ${Spacing[4]};
`;

interface TypeDetailProps {
  report: Report;
  isRedacted: boolean;
  tags?: Tag[];
  handleUpdateReportTags?: (reportId: string, tags: string[]) => void;
}

interface ReportDetailsSummaryProps extends TypeDetailProps {
  reportTemplate: ReportTemplate | undefined;
  featureFlags: FeatureFlagsMap;
  accordion?: boolean;
  isDefaultOpen?: boolean;
  showConnectedReportMenu?: boolean;
}

const StatusContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing[2]} ${Spacing[4]};
  border-radius: ${BORDER_RADIUSES.pill};
  box-sizing: border-box;
  width: fit-content;

  > svg {
    height: 20px;
    width: 20px;
  }
`;

const PendingStatusContainer = styled(StatusContainer)`
  background-color: ${COLORS.yellow10};
`;

const ErrorStatusContainer = styled(StatusContainer)`
  background-color: ${COLORS.red10};
`;

const StyledStatus = styled('h4')`
  padding-left: ${Spacing[2]};
`;

const getReportStatusTypeHeaderPill = (report: Report) => {
  if (report.attributes.status === ReportStatus.Pending) {
    return (
      <>
        <PendingStatusContainer>
          <CircleMinusIcon color={Color.BorderIconWarning} />
          <StyledStatus>Pending</StyledStatus>
        </PendingStatusContainer>
      </>
    );
  } else if (report.attributes.status === ReportStatus.Errored) {
    return (
      <>
        <ErrorStatusContainer>
          <CircleXIcon color={Color.BorderIconDanger} />
          <StyledStatus>Failed</StyledStatus>
        </ErrorStatusContainer>
      </>
    );
  } else {
    return null;
  }
};

const translateMissingRequiredFields = (missingRequiredFields: RequiredField[]) => {
  const missingFields = missingRequiredFields.map((field) => {
    switch (field) {
      case RequiredField.AddressStreet1:
        return 'Address street';
      case RequiredField.AddressCity:
        return 'Address city';
      case RequiredField.AddressSubdivision:
        return 'Address state / subdivision';
      case RequiredField.AddressPostalCode:
        return 'Address postal code';
      case RequiredField.Birthdate:
        return 'Birthdate';
      case RequiredField.AddressCountryCode:
      case RequiredField.CountryCode:
        return 'Country code';
      case RequiredField.EmailAddress:
        return 'Email address';
      case RequiredField.NameFirst:
        return 'First name';
      case RequiredField.NameLast:
        return 'Last name';
      case RequiredField.PhoneNumber:
        return 'Phone number';
      case RequiredField.SocialSecurityNumber:
        return 'Social security number';
      case RequiredField.Term:
        return 'Search term';
      default:
        assertNever(field);
    }
  });
  return missingFields.join(', ');
};

const TypeDetail = ({ report, isRedacted, tags, handleUpdateReportTags }: TypeDetailProps) => {
  if (report.attributes.status === ReportStatus.Errored) {
    const hasAllRequiredFields = isEmpty(report.attributes['missing-required-fields']);
    if (!hasAllRequiredFields) {
      return (
        <MissingRequiredFieldsSpan>
          {`Missing Field${
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            report.attributes['missing-required-fields'].length > 1 ? 's' : ''
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          }: ${translateMissingRequiredFields(report.attributes['missing-required-fields'])}`}
        </MissingRequiredFieldsSpan>
      );
    } else {
      return (
        <MissingRequiredFieldsSpan>Failure reason: Processing error</MissingRequiredFieldsSpan>
      );
    }
  }

  const showReportInput = !INPUTLESS_REPORTS.includes(report.type);

  if (isRedacted) {
    return (
      <Banner icon={<CircleAlertIcon />} css={{ zIndex: 1 }}>
        This report has been redacted
      </Banner>
    );
  }

  return (
    <>
      {showReportInput && (
        <>
          <Card.SectionHeader>Input</Card.SectionHeader>
          <ReportInput report={report} isRedacted={isRedacted} />
          <Spacer height={3} />
          <Card.SectionHeader>Results</Card.SectionHeader>
        </>
      )}
      <ReportResult report={report} isRedacted={isRedacted} multiCardFormat={false} />
      {tags && handleUpdateReportTags && (
        <>
          <Spacer height={1} />
          <ResourceTags
            tags={tags}
            tagScope={ApiModel.Report}
            editTagPermission={Permission.ReportWrite}
            onUpdateTags={(tags: string[]) => handleUpdateReportTags(report.id, tags)}
          />
        </>
      )}
    </>
  );
};

const AccordionTitleContainer = styled.div`
  width: 100%;
`;

const StyledCardHeader = styled(Card.Title)`
  padding: ${Spacing[5]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const ReportContent = styled.div`
  padding: 0 ${Spacing[2]};
`;

const ReportDetailsSummary = ({
  report,
  reportTemplate,
  isRedacted,
  tags,
  accordion = false,
  isDefaultOpen = true,
  showConnectedReportMenu = true,
  handleUpdateReportTags,
}: ReportDetailsSummaryProps) => {
  const { name: environmentName } = useCurrentEnvironmentType();

  return accordion ? (
    <ClickableDetail
      header={
        <AccordionTitleContainer
          data-test={`report-details-summary__title--${report.type}--${environmentName}`}
        >
          <Card.Title
            actions={
              showConnectedReportMenu
                ? [<ConnectedReportMenu key='connected-report-menu' report={report} />]
                : undefined
            }
          >
            <ReportModuleHeader report={report} reportTemplate={reportTemplate} />
          </Card.Title>
        </AccordionTitleContainer>
      }
      isDefaultOpen={isDefaultOpen}
      key={report.id}
    >
      <ReportContent>
        <TypeDetail
          report={report}
          isRedacted={isRedacted}
          tags={tags}
          handleUpdateReportTags={handleUpdateReportTags}
        />
      </ReportContent>
    </ClickableDetail>
  ) : (
    <Card data-test={`reports__${report.id}`}>
      <StyledCardHeader
        actions={[<ConnectedReportMenu key='connected-report-menu' report={report} />]}
      >
        <ReportModuleHeader report={report} reportTemplate={reportTemplate} />
      </StyledCardHeader>
      <Card.Body>
        {getReportStatusTypeHeaderPill(report)}
        <TypeDetail
          report={report}
          isRedacted={isRedacted}
          tags={tags}
          handleUpdateReportTags={handleUpdateReportTags}
        />
      </Card.Body>
    </Card>
  );
};

export default ReportDetailsSummary;
